// ===================================================================
// MainStreet — Re-entry Passes (Authorizations + Issue + Manage)
// Merged ReentryPasses + ReentryManager
// Enhanced with certificate modals, QR codes, and print/PDF support
// ===================================================================

import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Textarea,
  Select, MultiSelect, Modal, ActionIcon, Tooltip, CopyButton, Code,
  Stack, Divider, SegmentedControl, Progress,
} from '../../components/gab/GabComponents';
import { useDisclosure } from '../../components/gab/GabHooks';
import { notifications } from '../../lib/notifications';
import {
  IconPlus, IconRefresh, IconShieldCheck, IconBan, IconCopy, IconCheck,
  IconTicket, IconEye, IconPrinter, IconDownload, IconSearch,
  IconChevronDown, IconChevronRight,
} from '@tabler/icons-react';
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';
import { useMainStreetOpsStore, type MSReentryPass } from '../../store/mainstreetOpsStore';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { useAuthStore } from '../../store/authStore';
import { SECTOR_TYPES } from './constants';
import { primary, semantic } from '../../lib/colors';
import { fmtDate, fmtDateTime } from '../../lib/dateFormat';

// -- Helpers --------------------------------------------------------

const SECTOR_OPTIONS = SECTOR_TYPES.map(s => ({ value: s.id, label: s.label }));

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  issued: 'blue',
  verified: 'teal',
  revoked: 'red',
  expired: 'gray',
};

function generateCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'RE-';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

function getVerifyUrl(code: string): string {
  return `${window.location.origin}/mainstreet/verify/${encodeURIComponent(code)}`;
}

// -- Certificate Component ------------------------------------------

function PassCertificate({
  pass,
  authTitle,
  incidentName,
  qrDataUrl,
  certRef,
}: {
  pass: MSReentryPass;
  authTitle: string;
  incidentName: string;
  qrDataUrl: string;
  certRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const isExpired = pass.expiresAt ? new Date(pass.expiresAt) < new Date() : false;
  const isRevoked = pass.status === 'revoked';
  const isValid = !isExpired && !isRevoked;

  return (
    <div
      ref={certRef}
      style={{
        background: '#fff',
        border: '3px solid var(--color-primary-800)',
        borderRadius: 12,
        padding: 32,
        maxWidth: 520,
        margin: '0 auto',
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Top border accent */}
      <div style={{
        height: 6,
        background: 'linear-gradient(90deg, var(--color-primary-800), var(--color-primary-700), var(--color-primary-800))',
        borderRadius: 3,
        marginBottom: 24,
      }} />

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{
          fontSize: 13,
          letterSpacing: 3,
          color: primary[800],
          fontWeight: 700,
          marginBottom: 4,
        }}>
          OPENGOV EMERGENCY MANAGEMENT
        </div>
        <div style={{
          fontSize: 22,
          fontWeight: 800,
          color: primary[900],
          letterSpacing: 1,
        }}>
          MAINSTREET RE-ENTRY PASS
        </div>
      </div>

      {/* QR Code */}
      {qrDataUrl && (
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <img
            src={qrDataUrl}
            alt="QR Code"
            style={{ width: 180, height: 180, border: '1px solid #e0e0e0', borderRadius: 8 }}
          />
        </div>
      )}

      {/* Pass Code */}
      <div style={{
        textAlign: 'center',
        marginBottom: 20,
        padding: '12px 0',
        background: '#f8f6ff',
        borderRadius: 8,
        border: '1px solid #e0d8ff',
      }}>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
          Pass Code
        </div>
        <div style={{
          fontSize: 32,
          fontWeight: 800,
          fontFamily: "'DM Mono', monospace",
          letterSpacing: 4,
          color: primary[900],
        }}>
          {pass.code}
        </div>
      </div>

      {/* Status Badge */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <span style={{
          display: 'inline-block',
          padding: '6px 24px',
          borderRadius: 20,
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: 1,
          color: '#fff',
          background: isValid ? '#07963F' : semantic.error,
        }}>
          {isRevoked ? 'REVOKED' : isExpired ? 'EXPIRED' : 'VALID'}
        </span>
      </div>

      {/* Details Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px 24px',
        marginBottom: 20,
        fontSize: 13,
      }}>
        <div>
          <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
            Organization
          </div>
          <div style={{ fontWeight: 600, color: '#333' }}>{pass.orgName || '-'}</div>
        </div>
        <div>
          <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
            Authorization
          </div>
          <div style={{ fontWeight: 600, color: '#333' }}>{authTitle || '-'}</div>
        </div>
        {incidentName && (
          <div>
            <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
              Incident
            </div>
            <div style={{ fontWeight: 600, color: '#333' }}>{incidentName}</div>
          </div>
        )}
        <div>
          <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
            Issued
          </div>
          <div style={{ fontWeight: 600, color: '#333' }}>{fmtDate(pass.issuedAt)}</div>
        </div>
        <div>
          <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
            Expires
          </div>
          <div style={{
            fontWeight: 600,
            color: isExpired ? 'var(--color-error)' : '#333',
          }}>
            {fmtDate(pass.expiresAt)}
          </div>
        </div>
        {pass.verifiedAt && (
          <div>
            <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
              Verified
            </div>
            <div style={{ fontWeight: 600, color: '#333' }}>{fmtDateTime(pass.verifiedAt)}</div>
          </div>
        )}
      </div>

      {/* Bottom border accent */}
      <div style={{
        height: 6,
        background: 'linear-gradient(90deg, var(--color-primary-800), var(--color-primary-700), var(--color-primary-800))',
        borderRadius: 3,
        marginTop: 24,
      }} />

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: 12 }}>
        <div style={{ fontSize: 10, color: '#999', letterSpacing: 0.5 }}>
          Verify at: {getVerifyUrl(pass.code)}
        </div>
      </div>
    </div>
  );
}

// ===================================================================
export default function Reentry() {
  const {
    reentryAuths, reentryPasses, loading,
    loadReentryAuths, loadReentryPasses,
    createReentryAuth, issuePass, bulkIssuePasses, updatePassStatus,
  } = useMainStreetOpsStore();
  const { organizations, loadOrganizations, orgLists, loadOrgLists } = useMainStreetStore();
  const { activeIncident, user } = useAuthStore();

  // Auth create modal
  const [authOpened, { open: openAuth, close: closeAuth }] = useDisclosure(false);
  const [authTitle, setAuthTitle] = useState('');
  const [authTargetType, setAuthTargetType] = useState('all');
  const [authSectors, setAuthSectors] = useState<string[]>([]);
  const [authOrgs, setAuthOrgs] = useState<string[]>([]);
  const [authList, setAuthList] = useState<string | null>(null);
  const [authExpires, setAuthExpires] = useState('');
  const [authNotes, setAuthNotes] = useState('');
  const [savingAuth, setSavingAuth] = useState(false);

  // Issue pass modal
  const [issueOpened, { open: openIssue, close: closeIssue }] = useDisclosure(false);
  const [issueAuthId, setIssueAuthId] = useState('');
  const [issueOrgId, setIssueOrgId] = useState('');
  const [issueMode, setIssueMode] = useState<'single' | 'all' | 'sector' | 'list' | 'specific'>('single');
  const [issueSectors, setIssueSectors] = useState<string[]>([]);
  const [issueList, setIssueList] = useState<string | null>(null);
  const [issueOrgs, setIssueOrgs] = useState<string[]>([]);
  const [issuingPass, setIssuingPass] = useState(false);
  const [issueProgress, setIssueProgress] = useState({ done: 0, total: 0 });

  // Certificate modal
  const [certPass, setCertPass] = useState<MSReentryPass | null>(null);
  const [certOpened, { open: openCert, close: closeCert }] = useDisclosure(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const certRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadReentryAuths();
    loadReentryPasses();
    loadOrganizations();
    loadOrgLists();
  }, []);

  const orgOptions = useMemo(() =>
    organizations.map(o => ({ value: String(o.id), label: o.name })),
    [organizations],
  );

  // Show all auths in dropdown, marking inactive ones
  const authOptions = useMemo(() =>
    reentryAuths.map(a => ({
      value: String(a.Rid),
      label: a.status === 'active' ? a.title : `${a.title} (${a.status})`,
      disabled: a.status !== 'active',
    })),
    [reentryAuths],
  );

  const [passSearch, setPassSearch] = useState('');
  const [expandedAuths, setExpandedAuths] = useState<Set<string>>(new Set());

  const toggleAuthGroup = (authId: string) => {
    setExpandedAuths(prev => {
      const next = new Set(prev);
      if (next.has(authId)) next.delete(authId); else next.add(authId);
      return next;
    });
  };

  // Look up auth title for a pass
  const getAuthTitle = useCallback((authId: string) => {
    const auth = reentryAuths.find(a => String(a.Rid) === authId || String(a.id) === authId);
    return auth?.title ?? '';
  }, [reentryAuths]);

  const sortedPasses = useMemo(() => {
    let list = [...reentryPasses];
    if (passSearch) {
      const q = passSearch.toLowerCase();
      list = list.filter(p =>
        (p.orgName || '').toLowerCase().includes(q) ||
        (p.code || '').toLowerCase().includes(q) ||
        (p.status || '').toLowerCase().includes(q) ||
        getAuthTitle(p.authId).toLowerCase().includes(q),
      );
    }
    return list.sort((a, b) => (b.issuedAt ?? '').localeCompare(a.issuedAt ?? ''));
  }, [reentryPasses, passSearch, getAuthTitle]);

  // Group passes by authId for batch display
  const passGroups = useMemo(() => {
    const groups = new Map<string, MSReentryPass[]>();
    for (const p of sortedPasses) {
      const key = p.authId || '_none';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(p);
    }
    // Sort groups by most recent issuedAt
    return [...groups.entries()].sort((a, b) => {
      const aDate = a[1][0]?.issuedAt ?? '';
      const bDate = b[1][0]?.issuedAt ?? '';
      return bDate.localeCompare(aDate);
    });
  }, [sortedPasses]);

  // Generate QR code when viewing a certificate
  const viewCertificate = useCallback(async (pass: MSReentryPass) => {
    setCertPass(pass);
    try {
      const url = getVerifyUrl(pass.code);
      const dataUrl = await QRCode.toDataURL(url, {
        width: 200,
        margin: 2,
        color: { dark: primary[900], light: '#ffffff' },
      });
      setQrDataUrl(dataUrl);
    } catch {
      setQrDataUrl('');
    }
    openCert();
  }, [openCert]);

  // -- Create Authorization -----------------------------------------

  async function handleCreateAuth() {
    if (!authTitle.trim()) {
      notifications.show({ color: 'red', title: 'Required', message: 'Title is required.' });
      return;
    }
    setSavingAuth(true);
    // Resolve list to specific org IDs
    let resolvedTargetType = authTargetType;
    let resolvedTargetOrgs = authOrgs.join(',');
    if (authTargetType === 'list' && authList) {
      const list = orgLists.find(l => String(l.id) === authList);
      if (list) { resolvedTargetType = 'specific'; resolvedTargetOrgs = list.orgIds; }
    }
    await createReentryAuth({
      incidentId: activeIncident ? String(activeIncident.id) : '',
      title: authTitle.trim(),
      targetType: resolvedTargetType,
      targetSectors: authSectors.join(','),
      targetOrgs: resolvedTargetOrgs,
      issuedBy: user?.fullName ?? user?.email ?? 'Unknown',
      issuedAt: new Date().toISOString(),
      expiresAt: authExpires,
      status: 'active',
      notes: authNotes.trim(),
    });
    setSavingAuth(false);
    notifications.show({ color: 'green', title: 'Created', message: 'Re-entry authorization created.' });
    setAuthTitle(''); setAuthTargetType('all'); setAuthSectors([]);
    setAuthOrgs([]); setAuthExpires(''); setAuthNotes('');
    closeAuth();
  }

  // -- Resolve target orgs based on issue mode ----------------------

  function resolveTargetOrgs(): Array<{ id: string; name: string }> {
    switch (issueMode) {
      case 'single':
        if (!issueOrgId) return [];
        { const org = organizations.find(o => String(o.id) === issueOrgId);
          return org ? [{ id: String(org.id), name: org.name }] : []; }
      case 'all':
        return organizations.map(o => ({ id: String(o.id), name: o.name }));
      case 'sector':
        return organizations
          .filter(o => issueSectors.includes(o.sectorType || ''))
          .map(o => ({ id: String(o.id), name: o.name }));
      case 'list':
        if (!issueList) return [];
        { const list = orgLists.find(l => String(l.id) === issueList);
          if (!list) return [];
          const ids = list.orgIds.split(',').filter(Boolean);
          return organizations
            .filter(o => ids.includes(String(o.id)))
            .map(o => ({ id: String(o.id), name: o.name })); }
      case 'specific':
        return organizations
          .filter(o => issueOrgs.includes(String(o.id)))
          .map(o => ({ id: String(o.id), name: o.name }));
      default:
        return [];
    }
  }

  const issueTargetCount = useMemo(() => {
    switch (issueMode) {
      case 'single': return issueOrgId ? 1 : 0;
      case 'all': return organizations.length;
      case 'sector': return organizations.filter(o => issueSectors.includes(o.sectorType || '')).length;
      case 'list': {
        if (!issueList) return 0;
        const list = orgLists.find(l => String(l.id) === issueList);
        if (!list) return 0;
        return list.orgIds.split(',').filter(Boolean).length;
      }
      case 'specific': return issueOrgs.length;
      default: return 0;
    }
  }, [issueMode, issueOrgId, organizations, issueSectors, issueList, orgLists, issueOrgs]);

  // -- Issue Pass(es) ----------------------------------------------

  async function handleIssuePass() {
    if (!issueAuthId) {
      notifications.show({ color: 'red', title: 'Required', message: 'Select an authorization.' });
      return;
    }
    const targets = resolveTargetOrgs();
    if (targets.length === 0) {
      notifications.show({ color: 'red', title: 'No targets', message: 'No organizations selected for pass issuance.' });
      return;
    }

    const auth = reentryAuths.find(a => String(a.Rid) === issueAuthId || String(a.id) === issueAuthId);
    // Filter out orgs that already have an active pass for this auth
    const existingCodes = new Set(reentryPasses
      .filter(p => p.authId === issueAuthId && p.status !== 'revoked')
      .map(p => p.orgId));
    const newTargets = targets.filter(t => !existingCodes.has(t.id));

    if (newTargets.length === 0) {
      notifications.show({ color: 'orange', title: 'Already issued', message: 'All selected organizations already have active passes for this authorization.' });
      return;
    }

    const skipped = targets.length - newTargets.length;
    setIssuingPass(true);
    setIssueProgress({ done: 0, total: newTargets.length });

    const now = new Date().toISOString();

    if (newTargets.length === 1) {
      // Single pass — use original flow with certificate
      const target = newTargets[0];
      const code = generateCode();
      const newPass = await issuePass({
        authId: issueAuthId,
        orgId: target.id,
        orgName: target.name,
        code,
        status: 'issued',
        issuedAt: now,
        expiresAt: auth?.expiresAt ?? '',
        verifiedAt: '',
      });
      setIssuingPass(false);
      setIssueProgress({ done: 0, total: 0 });
      notifications.show({ color: 'green', title: 'Pass Issued', message: `Pass issued: ${code}` });
      setIssueAuthId(''); setIssueOrgId(''); setIssueMode('single');
      setIssueSectors([]); setIssueList(null); setIssueOrgs([]);
      closeIssue();
      if (newPass) viewCertificate(newPass);
      return;
    }

    // Bulk issuance — parallel batching, single reload
    const passData = newTargets.map(target => ({
      authId: issueAuthId,
      orgId: target.id,
      orgName: target.name,
      code: generateCode(),
      status: 'issued',
      issuedAt: now,
      expiresAt: auth?.expiresAt ?? '',
      verifiedAt: '',
    }));

    const issued = await bulkIssuePasses(passData, (done, total) => {
      setIssueProgress({ done, total });
    });

    setIssuingPass(false);
    setIssueProgress({ done: 0, total: 0 });
    const skipMsg = skipped > 0 ? ` (${skipped} already had passes)` : '';
    notifications.show({ color: 'green', title: 'Batch Complete', message: `${issued} passes issued${skipMsg}.` });
    setIssueAuthId(''); setIssueOrgId(''); setIssueMode('single');
    setIssueSectors([]); setIssueList(null); setIssueOrgs([]);
    closeIssue();
  }

  // -- Revoke pass --------------------------------------------------

  async function handleRevoke(id: number) {
    await updatePassStatus(id, 'revoked');
    notifications.show({ color: 'orange', title: 'Revoked', message: 'Pass revoked.' });
  }

  async function handleReinstate(id: number) {
    await updatePassStatus(id, 'issued');
    notifications.show({ color: 'green', title: 'Reinstated', message: 'Pass reinstated.' });
  }

  // -- Print --------------------------------------------------------

  function handlePrint() {
    const printContent = certRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Re-entry Pass</title>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet">
        <style>
          body { margin: 0; padding: 20px; font-family: 'DM Sans', sans-serif; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  }

  // -- PDF Download -------------------------------------------------

  async function handleDownloadPDF() {
    if (!certPass) return;

    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const centerX = pageWidth / 2;

      // Top accent line
      doc.setFillColor(44, 1, 214);
      doc.rect(20, 15, pageWidth - 40, 2, 'F');

      // Header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(44, 1, 214);
      doc.text('OPENGOV EMERGENCY MANAGEMENT', centerX, 28, { align: 'center' });

      doc.setFontSize(18);
      doc.setTextColor(25, 0, 155);
      doc.text('MAINSTREET RE-ENTRY PASS', centerX, 38, { align: 'center' });

      // QR Code
      if (qrDataUrl) {
        doc.addImage(qrDataUrl, 'PNG', centerX - 25, 45, 50, 50);
      }

      // Pass Code
      const codeY = qrDataUrl ? 105 : 55;
      doc.setFillColor(248, 246, 255);
      doc.roundedRect(40, codeY - 8, pageWidth - 80, 22, 3, 3, 'F');
      doc.setFontSize(8);
      doc.setTextColor(102, 102, 102);
      doc.text('PASS CODE', centerX, codeY - 1, { align: 'center' });
      doc.setFontSize(24);
      doc.setTextColor(25, 0, 155);
      doc.setFont('courier', 'bold');
      doc.text(certPass.code, centerX, codeY + 10, { align: 'center' });

      // Status
      const statusY = codeY + 24;
      const isExpired = certPass.expiresAt ? new Date(certPass.expiresAt) < new Date() : false;
      const isRevoked = certPass.status === 'revoked';
      const isValid = !isExpired && !isRevoked;
      const statusText = isRevoked ? 'REVOKED' : isExpired ? 'EXPIRED' : 'VALID';

      if (isValid) {
        doc.setFillColor(7, 150, 63);
      } else {
        doc.setFillColor(211, 52, 35);
      }
      const statusWidth = doc.getTextWidth(statusText) + 16;
      doc.roundedRect(centerX - statusWidth / 2, statusY - 4, statusWidth, 8, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text(statusText, centerX, statusY + 2, { align: 'center' });

      // Details
      let detailY = statusY + 16;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);

      const details: [string, string][] = [
        ['Organization', certPass.orgName || '-'],
        ['Authorization', getAuthTitle(certPass.authId) || '-'],
      ];
      if (activeIncident?.name) {
        details.push(['Incident', activeIncident.name]);
      }
      details.push(
        ['Issued', fmtDate(certPass.issuedAt)],
        ['Expires', fmtDate(certPass.expiresAt)],
      );
      if (certPass.verifiedAt) {
        details.push(['Verified', fmtDateTime(certPass.verifiedAt)]);
      }

      for (const [label, value] of details) {
        doc.setTextColor(136, 136, 136);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(label.toUpperCase(), 45, detailY);
        doc.setTextColor(51, 51, 51);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text(value, 45, detailY + 5);
        detailY += 14;
      }

      // Bottom accent line
      doc.setFillColor(44, 1, 214);
      doc.rect(20, detailY + 5, pageWidth - 40, 2, 'F');

      // Footer
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(153, 153, 153);
      doc.text(`Verify at: ${getVerifyUrl(certPass.code)}`, centerX, detailY + 14, { align: 'center' });

      doc.save(`reentry-pass-${certPass.code}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      notifications.show({ color: 'red', title: 'Error', message: 'Failed to generate PDF.' });
    }
  }

  // -- Render -------------------------------------------------------

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Re-entry Management</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loading} onClick={() => { loadReentryAuths(); loadReentryPasses(); }}>
            Refresh
          </Button>
          <Button size="xs" variant="light" leftSection={<IconTicket size={14} />} onClick={openIssue}>
            Issue Pass
          </Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openAuth}>
            New Authorization
          </Button>
        </Group>
      </Group>

      {/* -- Authorizations -------------------------------------------- */}
      <Title order={5} mb="xs">Authorizations ({reentryAuths.length})</Title>

      <Paper withBorder mb="md">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Title</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Target</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Issued By</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Issued</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Expires</th>
              </tr>
            </thead>
            <tbody>
              {reentryAuths.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 16 }}>
                    No authorizations created yet.
                  </td>
                </tr>
              )}
              {reentryAuths.map(auth => (
                <tr key={auth.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{auth.title}</td>
                  <td style={{ padding: '8px 12px' }}><Badge size="xs" variant="outline">{auth.targetType}</Badge></td>
                  <td style={{ padding: '8px 12px' }}><Badge size="xs" color={STATUS_COLORS[auth.status] ?? 'gray'}>{auth.status}</Badge></td>
                  <td style={{ padding: '8px 12px' }}>{auth.issuedBy || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{fmtDate(auth.issuedAt)}</td>
                  <td style={{ padding: '8px 12px' }}>{auth.expiresAt ? fmtDate(auth.expiresAt) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* -- Passes -------------------------------------------------- */}
      <Group justify="space-between" mt="lg" mb="sm">
        <Title order={5}>Issued Passes ({sortedPasses.length})</Title>
        <TextInput
          placeholder="Search org, code, status..."
          leftSection={<IconSearch size={14} />}
          value={passSearch}
          onChange={e => setPassSearch(e.target.value)}
          size="xs"
          style={{ minWidth: 220 }}
        />
      </Group>

      <Paper withBorder>
        <div style={{ overflowX: 'auto', maxHeight: 600, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Organization</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Code</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Issued</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Expires</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Verified</th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {passGroups.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                    {loading ? 'Loading...' : 'No passes issued'}
                  </td>
                </tr>
              )}
              {passGroups.map(([authId, passes]) => {
                const isBatch = passes.length > 1;
                const expanded = expandedAuths.has(authId);
                const authTitle = getAuthTitle(authId);
                const verified = passes.filter(p => p.verifiedAt).length;
                const revoked = passes.filter(p => p.status === 'revoked').length;
                const active = passes.filter(p => p.status === 'issued' && !(p.expiresAt && new Date(p.expiresAt) < new Date())).length;

                if (!isBatch) {
                  // Single pass — render directly
                  const pass = passes[0];
                  const isExpired = pass.expiresAt && new Date(pass.expiresAt) < new Date();
                  const displayStatus = isExpired && pass.status === 'issued' ? 'expired' : pass.status;
                  return (
                    <tr key={pass.id}
                      style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', cursor: 'pointer' }}
                      onClick={() => viewCertificate(pass)}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{pass.orgName || '-'}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <Group gap={4} wrap="nowrap">
                          <Code style={{ fontFamily: 'DM Mono, monospace', letterSpacing: 1 }}>{pass.code}</Code>
                          <CopyButton value={pass.code}>
                            {({ copied, copy }) => (
                              <ActionIcon size="xs" variant="subtle" color={copied ? 'green' : 'gray'}
                                onClick={(e) => { e.stopPropagation(); copy(); }}>
                                {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                              </ActionIcon>
                            )}
                          </CopyButton>
                        </Group>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <Badge size="xs" color={STATUS_COLORS[displayStatus] ?? 'gray'}>{displayStatus}</Badge>
                      </td>
                      <td style={{ padding: '8px 12px' }}>{fmtDate(pass.issuedAt)}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <Text size="xs" c={isExpired ? 'red' : undefined}>{fmtDate(pass.expiresAt)}</Text>
                      </td>
                      <td style={{ padding: '8px 12px' }}>{pass.verifiedAt ? fmtDateTime(pass.verifiedAt) : '-'}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <Group gap={4} justify="center" wrap="nowrap">
                          <Tooltip label="View Certificate">
                            <ActionIcon size="xs" color="blue" variant="subtle"
                              onClick={(e) => { e.stopPropagation(); viewCertificate(pass); }}>
                              <IconEye size={14} />
                            </ActionIcon>
                          </Tooltip>
                          {pass.status === 'issued' && !isExpired && (
                            <Tooltip label="Revoke">
                              <ActionIcon size="xs" color="red" variant="subtle"
                                onClick={(e) => { e.stopPropagation(); handleRevoke(pass.id); }}>
                                <IconBan size={14} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          {pass.status === 'revoked' && (
                            <Tooltip label="Reinstate">
                              <ActionIcon size="xs" color="green" variant="subtle"
                                onClick={(e) => { e.stopPropagation(); handleReinstate(pass.id); }}>
                                <IconCheck size={14} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Group>
                      </td>
                    </tr>
                  );
                }

                // Batch group — collapsible header row
                return (
                  <React.Fragment key={`group-${authId}`}>
                    <tr
                      style={{ borderBottom: '1px solid var(--mantine-color-gray-3)', background: 'var(--mantine-color-gray-0)', cursor: 'pointer' }}
                      onClick={() => toggleAuthGroup(authId)}>
                      <td style={{ padding: '8px 12px', fontWeight: 600 }} colSpan={2}>
                        <Group gap={6} wrap="nowrap">
                          {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                          <Badge size="xs" variant="filled" color="violet">Batch</Badge>
                          <Text size="sm" fw={600}>{authTitle || 'Authorization'}</Text>
                          <Badge size="xs" variant="light" color="gray">{passes.length} passes</Badge>
                        </Group>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <Group gap={4}>
                          {active > 0 && <Badge size="xs" color="blue">{active} active</Badge>}
                          {verified > 0 && <Badge size="xs" color="teal">{verified} verified</Badge>}
                          {revoked > 0 && <Badge size="xs" color="red">{revoked} revoked</Badge>}
                        </Group>
                      </td>
                      <td style={{ padding: '8px 12px' }}>{fmtDate(passes[0].issuedAt)}</td>
                      <td style={{ padding: '8px 12px' }}>{fmtDate(passes[0].expiresAt)}</td>
                      <td style={{ padding: '8px 12px' }}></td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <Tooltip label={`Revoke all ${active} active passes`}>
                          <ActionIcon size="xs" color="red" variant="subtle" disabled={active === 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!confirm(`Revoke all ${active} active passes in this batch?`)) return;
                              passes
                                .filter(p => p.status === 'issued')
                                .forEach(p => updatePassStatus(p.id, 'revoked'));
                            }}>
                            <IconBan size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </td>
                    </tr>
                    {expanded && passes.map(pass => {
                      const isExpired = pass.expiresAt && new Date(pass.expiresAt) < new Date();
                      const displayStatus = isExpired && pass.status === 'issued' ? 'expired' : pass.status;
                      return (
                        <tr key={pass.id}
                          style={{ borderBottom: '1px solid var(--mantine-color-gray-1)', cursor: 'pointer', background: '#fafbfc' }}
                          onClick={() => viewCertificate(pass)}>
                          <td style={{ padding: '6px 12px 6px 36px', fontWeight: 500, fontSize: '0.8rem' }}>{pass.orgName || '-'}</td>
                          <td style={{ padding: '6px 12px' }}>
                            <Group gap={4} wrap="nowrap">
                              <Code style={{ fontFamily: 'DM Mono, monospace', letterSpacing: 1, fontSize: '0.75rem' }}>{pass.code}</Code>
                              <CopyButton value={pass.code}>
                                {({ copied, copy }) => (
                                  <ActionIcon size="xs" variant="subtle" color={copied ? 'green' : 'gray'}
                                    onClick={(e) => { e.stopPropagation(); copy(); }}>
                                    {copied ? <IconCheck size={10} /> : <IconCopy size={10} />}
                                  </ActionIcon>
                                )}
                              </CopyButton>
                            </Group>
                          </td>
                          <td style={{ padding: '6px 12px' }}>
                            <Badge size="xs" color={STATUS_COLORS[displayStatus] ?? 'gray'}>{displayStatus}</Badge>
                          </td>
                          <td style={{ padding: '6px 12px', fontSize: '0.8rem' }}>{fmtDate(pass.issuedAt)}</td>
                          <td style={{ padding: '6px 12px' }}>
                            <Text size="xs" c={isExpired ? 'red' : undefined}>{fmtDate(pass.expiresAt)}</Text>
                          </td>
                          <td style={{ padding: '6px 12px', fontSize: '0.8rem' }}>{pass.verifiedAt ? fmtDateTime(pass.verifiedAt) : '-'}</td>
                          <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                            <Group gap={4} justify="center" wrap="nowrap">
                              <Tooltip label="View Certificate">
                                <ActionIcon size="xs" color="blue" variant="subtle"
                                  onClick={(e) => { e.stopPropagation(); viewCertificate(pass); }}>
                                  <IconEye size={12} />
                                </ActionIcon>
                              </Tooltip>
                              {pass.status === 'issued' && !isExpired && (
                                <Tooltip label="Revoke">
                                  <ActionIcon size="xs" color="red" variant="subtle"
                                    onClick={(e) => { e.stopPropagation(); handleRevoke(pass.id); }}>
                                    <IconBan size={12} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                              {pass.status === 'revoked' && (
                                <Tooltip label="Reinstate">
                                  <ActionIcon size="xs" color="green" variant="subtle"
                                    onClick={(e) => { e.stopPropagation(); handleReinstate(pass.id); }}>
                                    <IconCheck size={12} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Group>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* -- Create Authorization Modal ------------------------------ */}
      <Modal opened={authOpened} onClose={closeAuth} title="New Re-entry Authorization" size="lg">
        <TextInput label="Title" value={authTitle}
          onChange={e => setAuthTitle(e.target.value)}
          placeholder="e.g. Phase 1 Business Re-entry" size="sm" mb="sm" required />

        <Select label="Target Type" data={[
          { value: 'all', label: 'All Organizations' },
          { value: 'sector', label: 'By Sector' },
          { value: 'list', label: 'By List' },
          { value: 'specific', label: 'Specific Organizations' },
        ]} value={authTargetType} onChange={v => setAuthTargetType(v ?? 'all')} size="sm" mb="sm" />

        {authTargetType === 'sector' && (
          <MultiSelect label="Target Sectors" data={SECTOR_OPTIONS}
            value={authSectors} onChange={setAuthSectors}
            placeholder="Select sectors" size="sm" mb="sm" searchable />
        )}
        {authTargetType === 'list' && (
          <Select label="Target List" placeholder="Select a list..."
            data={orgLists.map(l => ({ value: String(l.id), label: `${l.name} (${l.orgIds.split(',').filter(Boolean).length} orgs)` }))}
            value={authList} onChange={setAuthList}
            size="sm" mb="sm" />
        )}
        {authTargetType === 'specific' && (
          <MultiSelect label="Target Organizations" data={orgOptions}
            value={authOrgs} onChange={setAuthOrgs}
            placeholder="Select organizations" size="sm" mb="sm" searchable />
        )}

        <TextInput label="Expires" type="date" value={authExpires}
          onChange={e => setAuthExpires(e.target.value)} size="sm" mb="sm" />

        <Textarea label="Notes" value={authNotes}
          onChange={e => setAuthNotes(e.target.value)}
          placeholder="Authorization notes..." size="sm" mb="md" autosize minRows={2} />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={closeAuth}>Cancel</Button>
          <Button loading={savingAuth} onClick={handleCreateAuth}
            leftSection={<IconShieldCheck size={16} />}>Create Authorization</Button>
        </Group>
      </Modal>

      {/* -- Issue Pass Modal ---------------------------------------- */}
      <Modal opened={issueOpened} onClose={closeIssue} title="Issue Re-entry Passes" size="md">
        <Stack gap="sm">
          <Select label="Authorization" data={authOptions} value={issueAuthId}
            onChange={v => setIssueAuthId(v ?? '')} placeholder="Select authorization"
            size="sm" required />

          <div>
            <Text size="sm" fw={500} mb={4}>Issue To</Text>
            <SegmentedControl
              value={issueMode}
              onChange={v => setIssueMode(v as typeof issueMode)}
              data={[
                { value: 'single', label: 'Single' },
                { value: 'all', label: 'All' },
                { value: 'sector', label: 'Sector' },
                { value: 'list', label: 'List' },
                { value: 'specific', label: 'Select' },
              ]}
              fullWidth
              size="sm"
            />
          </div>

          {issueMode === 'single' && (
            <Select label="Organization" data={orgOptions} value={issueOrgId}
              onChange={v => setIssueOrgId(v ?? '')} placeholder="Select organization"
              size="sm" searchable required />
          )}

          {issueMode === 'sector' && (
            <MultiSelect label="Sectors" data={SECTOR_OPTIONS} value={issueSectors}
              onChange={setIssueSectors} placeholder="Select sectors"
              size="sm" searchable />
          )}

          {issueMode === 'list' && (
            <Select label="Organization List" value={issueList}
              onChange={v => setIssueList(v)}
              data={orgLists.map(l => ({
                value: String(l.id),
                label: `${l.name} (${l.orgIds.split(',').filter(Boolean).length} orgs)`,
              }))}
              placeholder="Select a list"
              size="sm" searchable />
          )}

          {issueMode === 'specific' && (
            <MultiSelect label="Organizations" data={orgOptions} value={issueOrgs}
              onChange={setIssueOrgs} placeholder="Select organizations"
              size="sm" searchable />
          )}

          {issueMode === 'all' && (
            <Paper p="xs" bg="yellow.0" radius="sm" style={{ border: '1px solid var(--mantine-color-yellow-3)' }}>
              <Text size="xs" c="yellow.8">
                This will issue passes to <Text span fw={700}>all {organizations.length} organizations</Text> in the system.
              </Text>
            </Paper>
          )}

          <Text size="xs" c="dimmed">
            {issueTargetCount > 0
              ? `${issueTargetCount} organization${issueTargetCount > 1 ? 's' : ''} will receive a unique RE- pass code.`
              : 'Select target organizations above.'}
          </Text>

          {issuingPass && issueProgress.total > 0 && (
            <div>
              <Text size="xs" c="dimmed" mb={4}>Issuing {issueProgress.done} / {issueProgress.total}...</Text>
              <Progress value={(issueProgress.done / issueProgress.total) * 100} size="sm" color="green" animated />
            </div>
          )}

          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={closeIssue} disabled={issuingPass}>Cancel</Button>
            <Button loading={issuingPass} onClick={handleIssuePass}
              leftSection={<IconTicket size={16} />}
              disabled={issueTargetCount === 0 || !issueAuthId}>
              Issue {issueTargetCount > 1 ? `${issueTargetCount} Passes` : 'Pass'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* -- Certificate Modal --------------------------------------- */}
      <Modal
        opened={certOpened}
        onClose={closeCert}
        title="Re-entry Pass Certificate"
        size="lg"
        centered
      >
        {certPass && (
          <Stack gap="md">
            <PassCertificate
              pass={certPass}
              authTitle={getAuthTitle(certPass.authId)}
              incidentName={activeIncident?.name ?? ''}
              qrDataUrl={qrDataUrl}
              certRef={certRef}
            />

            <Divider />

            <Group justify="center" gap="sm">
              <Button
                variant="light"
                leftSection={<IconPrinter size={16} />}
                onClick={handlePrint}
              >
                Print
              </Button>
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={handleDownloadPDF}
              >
                Download PDF
              </Button>
              <CopyButton value={certPass.code}>
                {({ copied, copy }) => (
                  <Button
                    variant="light"
                    color={copied ? 'green' : 'gray'}
                    leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    onClick={copy}
                  >
                    {copied ? 'Copied!' : 'Copy Code'}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
}
